import { Move, MOVE_DAMAGE_CLASS_PHYSICAL, MOVE_DAMAGE_CLASS_SPECIAL, isBodyPressMove, isIkasamaMove, isKataYaburiMove, isPhotonGeyserMove, isPsyshockMove, isTerabrastMove } from "../../move/move";
import { OFFENCE_ITEM_ID_KODAWARIHATIMAKI, OFFENCE_ITEM_ID_KODAWARIMEGANE } from "../../types/item";
import { otherSetting, WEATHER_HARE, WEATHER_OOHIDERI, BATTLE_FIELD_EREKI } from "../../OtherSetting";
import { PokemonV2 } from "../../pokemon/pokemon";
import { POKEMON_TYPE_GRASS, POKEMON_TYPE_FIRE, POKEMON_TYPE_WATER, POKEMON_TYPE_BUG, POKEMON_TYPE_DRAGON, POKEMON_TYPE_ROCK, POKEMON_TYPE_STEEL, POKEMON_TYPE_ELECTRIC, POKEMON_TYPE_ICE, POKEMON_TYPE_GHOST, POKEMON_TYPE_FAIRY } from "../../types/type";
import { per_150, per_200, per_075, per_133_3, per_130_taranzisuta, per_050, per_130 } from "./constants";
import { calc_五捨五超入_gosutegoire, calcRankedValue } from "./core-utils";
import type { damageRateMapper } from "../../types/damage";
import { hasKodaiOrQuark, isAtsuiShibou, isDefeatistActive, isFairyAura, isGekiryu, isGorimutixyuu, isHadoronengine, isHaganetukai, isHarikiri, isHarikomi, isHihiironokodou, isKiyomenosio, isKonjou, isMouka, isMusinosirase, isRyunoagito, isSinryoku, isSlowStartActive, isSunpower, isSuihou, isTaineetu, isTaranzisuta, isTennen, isTikaramoti, isWazawainoohuda, isWazawainoutuwa } from "../ability";
import { isBurnedStatus, isPoisonedStatus } from "../pokemon/status";

// 【3】攻撃の補正値
export function calc_attack({
  move,
  offencePokemon,
  deffencePokemon,
  rateMapper,
  loopHitNumber,
  otherSetting,
  isCritical = false,
}: {
  move: Move;
  offencePokemon: PokemonV2;
  deffencePokemon: PokemonV2;
  rateMapper: damageRateMapper;
  loopHitNumber: number;
  otherSetting: otherSetting;
  isCritical?: boolean;
}) {
  let attack =
    move.damage_class_number === MOVE_DAMAGE_CLASS_PHYSICAL
      ? offencePokemon.effective_value.attack
      : offencePokemon.effective_value.special_attack;
  const isKatayaburiMove = isKataYaburiMove(move);
  const isPhotonGeyser = isPhotonGeyserMove(move);
  const isPsyshock = isPsyshockMove(move);
  const photonGeyserAttackRank = offencePokemon.rankCorrection?.attack ?? 0;
  const photonGeyserSpecialAttackRank = offencePokemon.rankCorrection?.special_attack ?? 0;
  const photonGeyserUsesAttack =
    isPhotonGeyser &&
    calcRankedValue(offencePokemon.effective_value.attack, photonGeyserAttackRank) >
    calcRankedValue(offencePokemon.effective_value.special_attack, photonGeyserSpecialAttackRank);
  const photonGeyserUsesSpecial = isPhotonGeyser && !photonGeyserUsesAttack;
  // 物理/特殊判定を特性補正でも統一して扱うために保持する

  if (isIkasamaMove(move)) {
    // イカサマ技は相手の攻撃力を使用
    attack = deffencePokemon.effective_value.attack
  } else if (isBodyPressMove(move)) {
    attack = offencePokemon.effective_value.defense
  } else if (isPsyshock) {
    // サイコショック系技は特攻値を使用
    attack = offencePokemon.effective_value.special_attack
  }
  // テラバーストは攻撃/特攻の高い方に合わせて物理/特殊扱いを切り替える
  // 選択技がテラバーストの場合のみ判定を行う
  const okAdjustTerabrast = isTerabrastMove(move) && isTerabrastMove(offencePokemon.selected_move) && offencePokemon.terasu_type &&
    calcRankedValue(offencePokemon.effective_value.attack, offencePokemon.rankCorrection.attack) >
    calcRankedValue(offencePokemon.effective_value.special_attack, offencePokemon.rankCorrection.special_attack)
  if (okAdjustTerabrast) {
    attack = offencePokemon.effective_value.attack
  }

  // フォトンゲイザー - ランク補正込みの攻撃・特攻を比較し、高い方を使用
  if (isPhotonGeyser) {
    const rankedAttack = calcRankedValue(offencePokemon.effective_value.attack, offencePokemon.rankCorrection.attack);
    const rankedSpecialAttack = calcRankedValue(offencePokemon.effective_value.special_attack, offencePokemon.rankCorrection.special_attack);
    
    if (rankedAttack > rankedSpecialAttack) {
      // 攻撃の方が高い場合は物理技として扱う
      attack = offencePokemon.effective_value.attack;
    } else {
      // 特攻の方が高いか同じ場合は特殊技として扱う
      attack = offencePokemon.effective_value.special_attack;
    }
  }


  // Apply rank correction (急所時は攻撃側のマイナス補正を0にする、てんねんは全ランク補正無視)
  if (isIkasamaMove(move)) {
    // イカサマ技は相手の攻撃ランクを使用
    let defenseAttackRank = isCritical && deffencePokemon.rankCorrection.attack < 0 ? 0 : deffencePokemon.rankCorrection.attack;
    
    // 相手の特性が「てんねん」の場合、相手自身の攻撃ランクは無視される
    if (isTennen(deffencePokemon) && !isKatayaburiMove) {
      defenseAttackRank = 0;
    }
    
    // 使用者の特性が「てんねん」の場合、相手の攻撃ランクは影響する（防御ランクのみ無視）
    // イカサマでは相手の攻撃ランクを使用するため、使用者のてんねんは攻撃ランクに影響しない
    
    attack = calcRankedValue(attack, defenseAttackRank)
  } else if (isBodyPressMove(move)) {
    let defenseRank = isCritical && offencePokemon.rankCorrection.defense < 0 ? 0 : offencePokemon.rankCorrection.defense;
    if (isTennen(deffencePokemon) && !isKatayaburiMove) {
      defenseRank = 0;
    }
    attack = calcRankedValue(attack, defenseRank)
  } else if (isPsyshock) {
    // サイコショック系技は特攻ランクを使用
    let specialAttackRank = isCritical && offencePokemon.rankCorrection.special_attack < 0 ? 0 : offencePokemon.rankCorrection.special_attack;
    if (isTennen(deffencePokemon) && !isKatayaburiMove) {
      specialAttackRank = 0;
    }
    attack = calcRankedValue(attack, specialAttackRank);
  } else if (isPhotonGeyser) {
    // フォトンゲイザーは既にランク補正込みの比較を行っているため、適切なランクを適用
    const baseAttackRank = (isTennen(deffencePokemon) && !isKatayaburiMove) ? 0 : offencePokemon.rankCorrection.attack;
    const baseSpecialAttackRank = (isTennen(deffencePokemon) && !isKatayaburiMove) ? 0 : offencePokemon.rankCorrection.special_attack;
    const rankedAttack = calcRankedValue(offencePokemon.effective_value.attack, baseAttackRank);
    const rankedSpecialAttack = calcRankedValue(offencePokemon.effective_value.special_attack, baseSpecialAttackRank);
    
    if (rankedAttack > rankedSpecialAttack) {
      // 物理技として扱う
      let attackRank = isCritical && offencePokemon.rankCorrection.attack < 0 ? 0 : offencePokemon.rankCorrection.attack;
      if (isTennen(deffencePokemon) && !isKatayaburiMove) {
        attackRank = 0;
      }
      attack = calcRankedValue(attack, attackRank);
    } else {
      // 特殊技として扱う
      let specialAttackRank = isCritical && offencePokemon.rankCorrection.special_attack < 0 ? 0 : offencePokemon.rankCorrection.special_attack;
      if (isTennen(deffencePokemon) && !isKatayaburiMove) {
        specialAttackRank = 0;
      }
      attack = calcRankedValue(attack, specialAttackRank);
    }
  } else if (move.damage_class_number === MOVE_DAMAGE_CLASS_PHYSICAL) {
    let attackRank = isCritical && offencePokemon.rankCorrection.attack < 0 ? 0 : offencePokemon.rankCorrection.attack;
    if (isTennen(deffencePokemon) && !isKatayaburiMove) {
      attackRank = 0;
    }
    attack = calcRankedValue(attack, attackRank);
  } else if (move.damage_class_number === MOVE_DAMAGE_CLASS_SPECIAL) {
    let specialAttackRank = isCritical && offencePokemon.rankCorrection.special_attack < 0 ? 0 : offencePokemon.rankCorrection.special_attack;
    if (isTennen(deffencePokemon) && !isKatayaburiMove) {
      specialAttackRank = 0;
    }
    attack = calcRankedValue(attack, specialAttackRank);
  }



  // 攻撃補正倍率は4096基準で積算
  let localRate = 4096;

  // とくせい
  // スロースタート・よわきは攻撃/特攻を半減する
  if (isSlowStartActive(offencePokemon)) {
    localRate = Math.floor(localRate * per_050);
  }
  if (isDefeatistActive(offencePokemon)) {
    localRate = Math.floor(localRate * per_050);
  }

  if (isSinryoku(offencePokemon) && move.type === POKEMON_TYPE_GRASS) {
    localRate = Math.floor(localRate * per_150);
  } else if (
    isMouka(offencePokemon) &&
    move.type === POKEMON_TYPE_FIRE
  ) {
    localRate = Math.floor(localRate * per_150);
  } else if (isGekiryu(offencePokemon) && move.type === POKEMON_TYPE_WATER) {
    localRate = Math.floor(localRate * per_150);
  } else if (isSuihou(offencePokemon) && move.type === POKEMON_TYPE_WATER) {
    localRate = Math.floor(localRate * per_200);
  } else if (
    isMusinosirase(offencePokemon) &&
    move.type === POKEMON_TYPE_BUG
  ) {
    localRate = Math.floor(localRate * per_150);
  } else if (
    isRyunoagito(offencePokemon) &&
    move.type === POKEMON_TYPE_DRAGON
  ) {
    localRate = Math.floor(localRate * per_150);
  } else if (
    offencePokemon.selected_ability?.name_ja === 'いわはこび' &&
    move.type === POKEMON_TYPE_ROCK
  ) {
    localRate = Math.floor(localRate * per_150);
  } else if (
    isHaganetukai(offencePokemon) &&
    move.type === POKEMON_TYPE_STEEL
  ) {
    localRate = Math.floor(localRate * per_150);
  } else if (
    isGorimutixyuu(offencePokemon) &&
    (move.damage_class_number === MOVE_DAMAGE_CLASS_PHYSICAL || 
     photonGeyserUsesAttack)
  ) {
    localRate = Math.floor(localRate * per_150);
  } else if (
    isTikaramoti(offencePokemon) &&
    (move.damage_class_number === MOVE_DAMAGE_CLASS_PHYSICAL ||
     photonGeyserUsesAttack)
  ) {
    localRate = Math.floor(localRate * per_200);
  } else if (
    isWazawainoutuwa(deffencePokemon) &&
    (move.damage_class_number === MOVE_DAMAGE_CLASS_SPECIAL || isPsyshock ||
     photonGeyserUsesSpecial) &&
    !isWazawainoutuwa(offencePokemon) // 相互無効化
  ) {
    localRate = Math.floor(localRate * per_075);
  } else if (
    isWazawainoohuda(deffencePokemon) &&
    (move.damage_class_number === MOVE_DAMAGE_CLASS_PHYSICAL ||
     photonGeyserUsesAttack) &&
    !isWazawainoohuda(offencePokemon) // 相互無効化
  ) {
    localRate = Math.floor(localRate * per_075);
  }

  // ひひいろのこどう - 天候が晴れの時かつ物理技の時に攻撃が1.333倍
  if (
    isHihiironokodou(offencePokemon) &&
    (otherSetting.weather === WEATHER_HARE || otherSetting.weather === WEATHER_OOHIDERI) &&
    (move.damage_class_number === MOVE_DAMAGE_CLASS_PHYSICAL ||
     photonGeyserUsesAttack)
  ) {
    localRate = Math.floor(localRate * per_133_3);
  }

  // ハドロンエンジン - エレキフィールドの時かつ特殊技の時に攻撃が1.333倍
  if (
    isHadoronengine(offencePokemon) &&
    otherSetting.battle_field === BATTLE_FIELD_EREKI &&
    (move.damage_class_number === MOVE_DAMAGE_CLASS_SPECIAL ||
     photonGeyserUsesSpecial)
  ) {
    localRate = Math.floor(localRate * per_133_3);
  }

  // フェアリーオーラ - フェアリー技の威力を1.333倍
  if (
    move.type === POKEMON_TYPE_FAIRY &&
    (isFairyAura(offencePokemon) || isFairyAura(deffencePokemon))
  ) {
    localRate = Math.floor(localRate * per_133_3);
  }

  // トランジスタ - でんきタイプの技の時に攻撃が1.3倍
  if (isTaranzisuta(offencePokemon) && move.type === POKEMON_TYPE_ELECTRIC) {
    localRate = Math.floor(localRate * per_130_taranzisuta);
  }

  // こだいかっせい・クォークチャージ - 攻撃/特攻選択による補正
  if (
    hasKodaiOrQuark(offencePokemon) &&
    offencePokemon.other_setting?.kodai_quark_stat === 'attack' &&
    (move.damage_class_number === MOVE_DAMAGE_CLASS_PHYSICAL ||
     photonGeyserUsesAttack)
  ) {
    localRate = Math.floor(localRate * per_130);
  } else if (
    hasKodaiOrQuark(offencePokemon) &&
    offencePokemon.other_setting?.kodai_quark_stat === 'special_attack' &&
    (move.damage_class_number === MOVE_DAMAGE_CLASS_SPECIAL ||
     photonGeyserUsesSpecial)
  ) {
    localRate = Math.floor(localRate * per_130);
  }

  // こんじょう - やけど・どく状態で物理技が1.5倍
  if (
    isKonjou(offencePokemon) &&
    (isBurnedStatus(offencePokemon.other_setting) || isPoisonedStatus(offencePokemon.other_setting)) &&
    (move.damage_class_number === MOVE_DAMAGE_CLASS_PHYSICAL ||
     photonGeyserUsesAttack)
  ) {
    localRate = Math.floor(localRate * per_150);
  }

  // サンパワー - 天気が晴れの時かつ特殊技の時に攻撃が1.5倍
  if (
    isSunpower(offencePokemon) &&
    (otherSetting.weather === WEATHER_HARE || otherSetting.weather === WEATHER_OOHIDERI) &&
    (move.damage_class_number === MOVE_DAMAGE_CLASS_SPECIAL ||
     photonGeyserUsesSpecial)
  ) {
    localRate = Math.floor(localRate * per_150);
  }


  // はりこみ - 攻撃・特殊技両方に2倍補正
  if (isHarikomi(offencePokemon)) {
    localRate = Math.floor(localRate * per_200);
  }

  // 防御側特性による技タイプ軽減
  // あついしぼう - ほのおタイプまたはこおりタイプの技を0.5倍
  if (
    isAtsuiShibou(deffencePokemon) &&
    (move.type === POKEMON_TYPE_FIRE || move.type === POKEMON_TYPE_ICE)
  ) {
    localRate = Math.floor(localRate * per_050);
  }

  // たいねつ - ほのおタイプの技を0.5倍
  if (
    isTaineetu(deffencePokemon) &&
    move.type === POKEMON_TYPE_FIRE
  ) {
    localRate = Math.floor(localRate * per_050);
  }

  // すいほう (防御側効果) - ほのおタイプの技を0.5倍
  if (isSuihou(deffencePokemon) && move.type === POKEMON_TYPE_FIRE) {
    localRate = Math.floor(localRate * per_050);
  }

  // きよめのしお - ゴーストタイプの技を0.5倍
  if (isKiyomenosio(deffencePokemon) && move.type === POKEMON_TYPE_GHOST) {
    localRate = Math.floor(localRate * per_050);
  }

  // アイテム
  if (
    offencePokemon.selected_item_id ===
    OFFENCE_ITEM_ID_KODAWARIHATIMAKI &&
    (move.damage_class_number === MOVE_DAMAGE_CLASS_PHYSICAL ||
     photonGeyserUsesAttack)
  ) {
    localRate = Math.floor(localRate * per_150);
  } else if (
    offencePokemon.selected_item_id ===
    OFFENCE_ITEM_ID_KODAWARIMEGANE &&
    (move.damage_class_number === MOVE_DAMAGE_CLASS_SPECIAL ||
     photonGeyserUsesSpecial)
  ) {
    localRate = Math.floor(localRate * per_150);
  }

  // 【4】最終攻撃
  // はりきり - 物理技の威力を1.5倍
  if (
    isHarikiri(offencePokemon) &&
    (move.damage_class_number === MOVE_DAMAGE_CLASS_PHYSICAL ||
     photonGeyserUsesAttack)
  ) {
    localRate = Math.floor(localRate * per_150);
  }

  let finalAttack = calc_五捨五超入_gosutegoire((attack * localRate) / 4096);
  if (finalAttack < 1) {
    finalAttack = 1;
  }



  return finalAttack;
}
